import { itemsApi } from "#/api/itemsApi";
import { CloseButton, Drawer, SegmentedControl, Skeleton, Table, Title } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconTrendingUp } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

const AUMHero = () => {
  const { t } = useTranslation();
  const [drawerOpened, { open: openDrawer, close: closeDrawer }] = useDisclosure(false);
  const [activeTab, setActiveTab] = useState<string>("product");

  const { data: aumData, isFetching } = useQuery({
    queryKey: ["aum"],
    queryFn: async () => {
      const response = await itemsApi.getAUM();
      return response.data;
    },
    staleTime: 1000 * 60,
  });

  const formatCurrency = (value: number) => {
    if (value >= 1000000) {
      return (
        <>
          <span className="text-[0.8em]">$</span>
          {(value / 1000000).toFixed(2)}M
        </>
      );
    } else if (value >= 1000) {
      return (
        <>
          <span className="text-[0.8em]">$</span>
          {(value / 1000).toFixed(1)}K
        </>
      );
    }
    return (
      <>
        <span className="text-[0.8em]">$</span>
        {value.toLocaleString()}
      </>
    );
  };

  return (
    <>
      <div className="w-full flex flex-col gap-2">
        <div
          className="border rounded-lg p-8 w-full cursor-pointer hover:shadow-lg transition-shadow"
          style={{
            borderColor: `var(--mantine-color-teal-3)`,
            backgroundColor: `var(--mantine-color-teal-0)`,
          }}
          onClick={openDrawer}
        >
          {isFetching ? (
            <Skeleton height={120} />
          ) : (
            <div className="flex items-center justify-between">
              <div className="flex flex-col gap-4 flex-1">
                <div className="flex items-center gap-3">
                  <div
                    className="p-3 rounded-lg"
                    style={{
                      backgroundColor: `var(--mantine-color-teal-1)`,
                    }}
                  >
                    <IconTrendingUp
                      size={26}
                      style={{ color: `var(--mantine-color-teal-7)` }}
                    />
                  </div>
                  <Title order={3} className="font-bold text-gray-800">
                    {t("dashboard.aum.title")}
                  </Title>
                </div>

                <div className="text-4xl font-extrabold" style={{ color: `var(--mantine-color-teal-7)` }}>
                  <span className="text-2xl">$</span>
                  {aumData?.total ? (
                    aumData.total >= 1000000 ? (
                      <>
                        {(aumData.total / 1000000).toFixed(2)}
                        <span className="text-2xl"> {t("dashboard.aum.million")}</span>
                      </>
                    ) : aumData.total >= 1000 ? (
                      <>
                        {(aumData.total / 1000).toFixed(1)}
                        <span className="text-2xl"> {t("dashboard.aum.thousand")}</span>
                      </>
                    ) : (
                      aumData.total.toLocaleString()
                    )
                  ) : (
                    '0'
                  )}
                </div>

                <div className="text-sm text-gray-600">
                  {t("dashboard.aum.totalValueDescription")}
                  <span className="ml-2 text-teal-600 font-medium">{t("dashboard.aum.clickToViewBreakdown")} →</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <Drawer
        opened={drawerOpened}
        onClose={closeDrawer}
        position="bottom"
        size="xl"
        withCloseButton={false}
      >
        <div className="w-full flex flex-col gap-4">
          <div className="w-full flex items-center justify-between border-b border-b-slate-300 pb-4">
            <div className="flex items-center gap-2">
              <Title order={3}>{t("dashboard.aum.breakdownTitle")}</Title>
              <SegmentedControl
                data={[
                  { label: t("dashboard.aum.byCategory"), value: "product" },
                  { label: t("dashboard.aum.bySite"), value: "site" }
                ]}
                size="xs"
                value={activeTab}
                onChange={setActiveTab}
              />
            </div>
            <CloseButton onClick={closeDrawer} />
          </div>

          {activeTab === "product" && (
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t("dashboard.aum.category")}</Table.Th>
                  <Table.Th>{t("dashboard.aum.totalValue")}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {aumData?.byProduct?.map((item) => (
                  <Table.Tr key={item.productId}>
                    <Table.Td>{item.productName}</Table.Td>
                    <Table.Td className="font-semibold text-teal-700">
                      {formatCurrency(item.totalValue)}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}

          {activeTab === "site" && (
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t("dashboard.aum.site")}</Table.Th>
                  <Table.Th>{t("dashboard.aum.totalValue")}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {aumData?.bySite?.map((item) => (
                  <Table.Tr key={item.siteId}>
                    <Table.Td>{item.siteName}</Table.Td>
                    <Table.Td className="font-semibold text-teal-700">
                      {formatCurrency(item.totalValue)}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </div>
      </Drawer>
    </>
  );
};

export default AUMHero;
